import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { message } from 'antd';
import momentPostApi from '../../api/momentPost';
import moment from 'moment';

// 格式化时间数组为字符串
const formatTimeArray = (timeArray) => {
  if (Array.isArray(timeArray) && timeArray.length >= 6) {
    return moment(new Date(timeArray[0], timeArray[1] - 1, timeArray[2], 
                           timeArray[3], timeArray[4], timeArray[5])).format('YYYY-MM-DD HH:mm:ss');
  }
  return '-';
};

// 分页查询动态
export const fetchMomentPostList = createAsyncThunk(
  'momentPost/fetchList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        page = 1, 
        pageSize = 10, 
        content,
        location,
        status,
        startTime,
        endTime,
        ...otherParams
      } = params;

      const queryParams = {
        page,
        pageSize,
        content,
        location,
        status,
        startTime,
        endTime,
        ...otherParams
      };

      const response = await momentPostApi.pageMomentPosts(queryParams);
      
      // 处理响应数据
      const processedData = {
        records: (response.data?.records || []).map(item => ({
          ...item,
          createTime: formatTimeArray(item.createTime),
          updateTime: formatTimeArray(item.updateTime),
          
          // 状态映射
          statusText: {
            'DELETED': '已删除',
            'AUDITING': '审核中',
            'APPROVED': '审核通过',
            'REJECTED': '审核驳回',
            'PRIVATE': '仅自己可见'
          }[item.status] || item.status,

          // 图片处理
          imageList: item.images || [],

          // 额外的统计信息
          likeCount: item.likeCount || 0,
          commentCount: item.commentCount || 0,
          shareCount: item.shareCount || 0,

          // 用户信息
          userId: item.userId,
          location: item.location || '未指定'
        })),
        total: response.data?.total || 0,
        current: response.data?.current || page,
        size: response.data?.size || pageSize,
        pages: response.data?.pages || 1
      };

      return processedData;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取动态列表失败' });
    }
  }
);

// 获取动态详情
export const fetchMomentPostDetail = createAsyncThunk(
  'momentPost/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await momentPostApi.getMomentPostDetail(id);
      
      // 处理详情数据
      const detail = response.data;
      
      return {
        ...detail,
        createTime: formatTimeArray(detail.createTime),
        updateTime: formatTimeArray(detail.updateTime),
        
        // 状态映射
        statusText: {
          'DELETED': '已删除',
          'AUDITING': '审核中',
          'APPROVED': '审核通过',
          'REJECTED': '审核驳回',
          'PRIVATE': '仅自己可见'
        }[detail.status] || detail.status,

        // 图片处理
        imageList: detail.images || [],

        // 额外的统计信息
        likeCount: detail.likeCount || 0,
        commentCount: detail.commentCount || 0,
        shareCount: detail.shareCount || 0,

        // 用户信息
        userId: detail.userId,
        location: detail.location || '未指定'
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取动态详情失败' });
    }
  }
);

// 更新动态状态
export const updateMomentPostStatus = createAsyncThunk(
  'momentPost/updateStatus',
  async ({ id, status }, { rejectWithValue, dispatch }) => {
    try {
      const response = await momentPostApi.updateMomentPostStatus(id, { status });
      
      // 刷新列表
      dispatch(fetchMomentPostList());
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '更新动态状态失败' });
    }
  }
);

// 创建动态
export const createMomentPost = createAsyncThunk(
  'momentPost/create',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await momentPostApi.createMomentPost(data);
      
      message.success('创建动态成功');
      
      // 刷新列表
      dispatch(fetchMomentPostList());
      
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '创建动态失败';
      message.error(errorMessage);
      return rejectWithValue(error.response?.data || { message: errorMessage });
    }
  }
);

// 删除动态
export const deleteMomentPost = createAsyncThunk(
  'momentPost/delete',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await momentPostApi.deleteMomentPost(id);
      
      message.success('删除动态成功');
      
      // 刷新列表
      dispatch(fetchMomentPostList());
      
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || '删除动态失败';
      message.error(errorMessage);
      return rejectWithValue(error.response?.data || { message: errorMessage });
    }
  }
);

const momentPostSlice = createSlice({
  name: 'momentPost',
  initialState: {
    momentPosts: [],
    total: 0,
    current: 1,
    pageSize: 10,
    loading: false,
    error: null,
    currentMomentPost: null,
    momentPostDetail: null,
    detailLoading: false,
    detailError: null
  },
  reducers: {
    setCurrentMomentPost: (state, action) => {
      state.currentMomentPost = action.payload;
    }
  },
  extraReducers: (builder) => {
    // 获取动态列表
    builder.addCase(fetchMomentPostList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMomentPostList.fulfilled, (state, action) => {
      state.loading = false;
      state.momentPosts = action.payload.records;
      state.total = action.payload.total;
      state.current = action.payload.current;
      state.pageSize = action.payload.size;
    });
    builder.addCase(fetchMomentPostList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 获取动态详情
    builder.addCase(fetchMomentPostDetail.pending, (state) => {
      state.detailLoading = true;
      state.detailError = null;
    });
    builder.addCase(fetchMomentPostDetail.fulfilled, (state, action) => {
      state.detailLoading = false;
      state.momentPostDetail = action.payload;
    });
    builder.addCase(fetchMomentPostDetail.rejected, (state, action) => {
      state.detailLoading = false;
      state.detailError = action.payload;
    });

    // 创建动态
    builder.addCase(createMomentPost.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createMomentPost.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createMomentPost.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 删除动态
    builder.addCase(deleteMomentPost.fulfilled, (state) => {
      // 可以在这里添加额外的状态更新逻辑
    });
  }
});

export const { setCurrentMomentPost } = momentPostSlice.actions;
export default momentPostSlice.reducer;
